// src/contexts/auth/hooks/boot.ts
/** @jsxImportSource @emotion/react */

import { useCallback } from "react";
import type { Session, SetAuthStateInput, User, RegistrationData } from "../types";
import type { Profile } from "../../../api/profile/profile.types";
import { getHashOrQueryParams, scrubMagicLinkFromUrl } from "../helpers";

import {
    api,
    clearSession as clearApiSession,
    setSession as setApiSession,
} from "../../../api/api";
import { bootApi, type BootResponse } from "../../../api/boot/boot";
import { makeAuthLogger } from "./storage";
import { isAccessTokenExpired, isIdleTooLong, readStoredSession } from "../../../api";

import {
    clearAuthHandoffCookie,
    handoffPayloadToSetAuthStateInput,
    readAuthHandoffCookie,
} from "../cookie-session";

/**
 * Fetch current user. Use /profiles/me since it contains user data.
 */
async function fetchCurrentUser(): Promise<User | null> {
    try {
        const me = await api.get("/profiles/me");
        return (me?.data ?? null) as any as User;
    } catch {
        return null;
    }
}

/**
 * Dispatch boot data events so PaymentContext and AppBootContext
 * can seed their state without separate network calls.
 */
function dispatchBootEvents(bootData: BootResponse) {
    if (bootData.payment) {
        window.dispatchEvent(
            new CustomEvent("boot:payment", { detail: bootData.payment }),
        );
    }
    if (bootData.canonical_conversation) {
        window.dispatchEvent(
            new CustomEvent("boot:conversation", {
                detail: bootData.canonical_conversation,
            }),
        );
    }
}

export const useAuthBoot = (deps: {
    setError: (v: string | null) => void;
    setSessionLoading: (v: boolean) => void;

    setSession: (v: Session | null) => void;
    setUser: (v: User | null) => void;

    // NOTE: kept for compatibility, but NOT used in boot.
    emitAuthChange: () => void;

    setAuthStateFromTokens: (tokens: SetAuthStateInput) => Promise<void>;
    tryRefreshIfNeeded: () => Promise<any | null>;
    syncSession: (bootData?: { profile: Profile }) => Promise<{ id: string; data: RegistrationData; profile: Profile } | null>;
}) => {
    const {
        setError,
        setSessionLoading,
        setSession,
        setUser,
        setAuthStateFromTokens,
        syncSession,
        tryRefreshIfNeeded,
    } = deps;

    const L = makeAuthLogger("boot");

    const boot = useCallback(async () => {
        L.log("[boot] called");

        let cancelled = false;

        const run = async () => {
            setError(null);

            try {
                /**
                 * 0) ✅ Cookie handoff hydration (Safari tab ↔ standalone)
                 * If present and fresh, hydrate into THIS context.
                 */
                const handoff = readAuthHandoffCookie();
                if (handoff?.access_token && handoff?.refresh_token) {
                    const ageMs = Date.now() - (handoff.created_at_ms ?? 0);
                    const isFresh = ageMs <= 30 * 60 * 1000;

                    L.log("[boot] handoff cookie present", { isFresh, ageMs });

                    if (isFresh) {
                        const input = handoffPayloadToSetAuthStateInput(
                            handoff,
                        );

                        // Ensure axios points at these tokens too (NO emit)
                        setApiSession(input.access_token, input.refresh_token, {
                            emit: false,
                        });

                        await setAuthStateFromTokens(input);

                        // Clear cookie so it won't re-run every load
                        clearAuthHandoffCookie();

                        // Load profile + payment + conversation via boot endpoint.
                        try {
                            const bootData = await bootApi.getMe();
                            await syncSession({ profile: bootData.profile });
                            dispatchBootEvents(bootData);
                        } catch {
                            L.warn("[boot] boot endpoint failed on handoff, falling back to syncSession");
                            await syncSession();
                        }
                        return;
                    } else {
                        L.log("[boot] handoff cookie too old -> clearing");
                        clearAuthHandoffCookie();
                    }
                }

                // 1) Magic link hash/query tokens
                const params = getHashOrQueryParams();
                const hashAccess = params["access_token"];
                const hashRefresh = params["refresh_token"];
                const hashExpiresIn = params["expires_in"];
                const hashExpiresAt = params["expires_at"];

                if (hashAccess && hashRefresh) {
                    try {
                        L.log("[boot] magiclink detected -> set session");

                        const expires_in_num = hashExpiresIn &&
                                Number.isFinite(Number(hashExpiresIn))
                            ? Number(hashExpiresIn)
                            : undefined;

                        const expires_at_num = hashExpiresAt &&
                                Number.isFinite(Number(hashExpiresAt))
                            ? Number(hashExpiresAt)
                            : undefined;

                        // ✅ set axios auth header + storage (NO emit)
                        setApiSession(hashAccess, hashRefresh, {
                            ...(typeof expires_in_num === "number"
                                ? { expires_in: expires_in_num }
                                : {}),
                            ...(typeof expires_at_num === "number"
                                ? { expires_at: expires_at_num }
                                : {}),
                            emit: false,
                        });

                        const payload: SetAuthStateInput = {
                            access_token: hashAccess,
                            refresh_token: hashRefresh,
                            ...(typeof expires_in_num === "number"
                                ? { expires_in: expires_in_num }
                                : {}),
                            ...(typeof expires_at_num === "number"
                                ? { expires_at: expires_at_num }
                                : {}),
                        };

                        await setAuthStateFromTokens(payload);
                        try {
                            const bootData = await bootApi.getMe();
                            await syncSession({ profile: bootData.profile });
                            dispatchBootEvents(bootData);
                        } catch {
                            L.warn("[boot] boot endpoint failed on magiclink, falling back to syncSession");
                            await syncSession();
                        }
                    } finally {
                        scrubMagicLinkFromUrl();
                    }
                    return;
                }

                // 2) Boot from localStorage
                const stored = readStoredSession();

                L.log("[boot] stored session snapshot", {
                    hasAccess: !!stored.access_token,
                    hasRefresh: !!stored.refresh_token,
                    expires_at: stored.expires_at,
                    user_id: stored.user_id,
                });

                if (!stored.access_token || !stored.refresh_token) {
                    L.log(
                        "[boot] no stored tokens -> clear in-memory and syncSession()",
                    );
                    if (!cancelled) {
                        setSession(null);
                        setUser(null);
                    }
                    await syncSession();
                    return;
                }

                // If the user was idle too long, clear session and don't restore.
                // They'll land on the login page (biometric or email based on stored prefs).
                if (isIdleTooLong()) {
                    L.log("[boot] idle too long -> clearing session");
                    clearApiSession({ emit: false });
                    if (!cancelled) {
                        setSession(null);
                        setUser(null);
                    }
                    await syncSession();
                    return;
                }

                // ✅ Ensure axios default header matches stored tokens (NO emit)
                setApiSession(stored.access_token, stored.refresh_token, {
                    emit: false,
                });

                // 3) Refresh only when the access token is expired or
                //    close to expiry (< 5 min remaining).
                //    If the token is still fresh, skip straight to
                //    syncSession — the proactive refresh timer, visibility
                //    handler, and 401 interceptor cover revoked-session
                //    detection for the remaining token lifetime.
                {
                    const nowSec = Math.floor(Date.now() / 1000);
                    const remainingSec = stored.expires_at
                        ? stored.expires_at - nowSec
                        : 0;
                    const needsRefresh =
                        isAccessTokenExpired(stored.expires_at) ||
                        remainingSec < 300; // 5 min buffer

                    if (needsRefresh) {
                        L.log(
                            `[boot] token ${
                                isAccessTokenExpired(stored.expires_at)
                                    ? "expired"
                                    : "expiring soon"
                            } -> tryRefreshIfNeeded()`,
                        );
                        const refreshed = await tryRefreshIfNeeded();
                        if (!refreshed) {
                            L.warn("[boot] refresh failed -> clear session");
                            clearApiSession({ emit: false });
                            if (!cancelled) {
                                setSession(null);
                                setUser(null);
                            }
                            await syncSession();
                            return;
                        }
                    } else {
                        L.log(
                            `[boot] access token still valid (${remainingSec}s remaining) -> skipping refresh`,
                        );
                    }
                }

                // 4) Load profile + payment + conversation via combined boot endpoint
                const after = readStoredSession();

                let profileResult;
                try {
                    const bootData = await bootApi.getMe();
                    profileResult = await syncSession({ profile: bootData.profile });
                    dispatchBootEvents(bootData);
                } catch {
                    L.warn("[boot] boot endpoint failed, falling back to syncSession");
                    profileResult = await syncSession();
                }

                const meUser = profileResult ? (profileResult.profile as any as User) : await fetchCurrentUser();

                if (!cancelled) {
                    setUser(meUser ?? null);
                    setSession({
                        access_token: after.access_token,
                        refresh_token: after.refresh_token,
                        expires_in: 0,
                        user: meUser ?? null,
                    });

                    // ❗ Do NOT emit auth:change during boot

                    // Mark device as having a registered account for returning-user detection
                    if (profileResult?.profile?.registration_completed) {
                        try { localStorage.setItem("mailander_has_account", "true"); } catch {}
                    }
                }
            } catch (e: any) {
                L.error("[boot] error", e);
                // Prevent biometric login loop on next visit
                try { localStorage.setItem("block_biometric_login", "true"); } catch {}
                if (!cancelled) {
                    setError(String(e?.message || "Auth boot failed."));
                }
            } finally {
                if (!cancelled) setSessionLoading(false);
            }
        };

        await run();

        return () => {
            cancelled = true;
            L.log("[boot] cleanup -> cancelled = true");
        };
    }, [
        L,
        setError,
        setSessionLoading,
        setSession,
        setUser,
        setAuthStateFromTokens,
        syncSession,
        tryRefreshIfNeeded,
    ]);

    return { boot };
};
